/**
 * User Cache Service
 *
 * Provides persistent caching for user lists to improve performance
 * when dealing with large datasets. Uses localStorage for small data
 * and IndexedDB for large datasets (>1MB) with automatic fallback.
 *
 * Cache Strategy:
 * - Shows cached data immediately (instant UI feedback)
 * - Fetches fresh data in background (SWR handles this)
 * - Cache expires after configured time (default: 1 hour)
 * - Cache versioning for invalidation on API changes
 * - Automatic storage selection: localStorage (<1MB) or IndexedDB (>1MB)
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

const CACHE_VERSION = '1.0.0';
const DEFAULT_TTL_MS = 60 * 60 * 1000 * 24 * 7; // 7 days
const MAX_LOCALSTORAGE_SIZE = 1024 * 1024; // 1MB threshold for IndexedDB
const DB_NAME = 'DitioUserCache';
const DB_VERSION = 1;
const STORE_NAME = 'cacheEntries';

// Cache keys (scoped by company ID)
const CACHE_KEYS = {
  USERS_V3: (companyId: string, status: string) =>
    `users_v3_${companyId}_${status}`,
  USERS_V4: (
    companyId: string,
    includeInactive: boolean,
    ignoreFiltering: boolean
  ) => `users_v4_${companyId}_${includeInactive}_${ignoreFiltering}`,
} as const;

/**
 * IndexedDB Helper - Opens database connection
 */
const openDB = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: 'key',
        });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });

/**
 * Check if IndexedDB is available
 */
const isIndexedDBAvailable = (): boolean =>
  typeof window !== 'undefined' && !!window.indexedDB;

class UserCacheService {
  private static dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Get IndexedDB instance (with caching)
   */
  private static getDB(): Promise<IDBDatabase> | null {
    if (!isIndexedDBAvailable()) {
      return null;
    }

    if (!this.dbPromise) {
      this.dbPromise = openDB().catch((error) => {
        this.dbPromise = null;
        throw error;
      });
    }

    return this.dbPromise;
  }

  /**
   * Get cached user data (synchronous - checks localStorage first)
   * For IndexedDB entries, use getAsync() instead
   * Returns null if cache is expired, invalid, or doesn't exist
   */
  static get<T>(key: string, ttl: number = DEFAULT_TTL_MS): T | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(stored);

      // Check version
      if (entry.version !== CACHE_VERSION) {
        this.remove(key);
        return null;
      }

      // Check expiration
      const age = Date.now() - entry.timestamp;
      if (age > ttl) {
        this.remove(key);
        return null;
      }

      return entry.data;
    } catch {
      // If parsing fails, remove corrupted cache
      this.remove(key);
      return null;
    }
  }

  /**
   * Get cached data from IndexedDB (async)
   * Use this for large datasets stored in IndexedDB
   */
  static async getAsync<T>(
    key: string,
    ttl: number = DEFAULT_TTL_MS
  ): Promise<T | null> {
    const dbPromise = this.getDB();
    if (!dbPromise) {
      return null;
    }

    try {
      const db = await dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      return new Promise<T | null>((resolve) => {
        request.onsuccess = () => {
          const entry = request.result as
            | (CacheEntry<T> & { key: string })
            | undefined;

          if (!entry) {
            resolve(null);
            return;
          }

          // Check version
          if (entry.version !== CACHE_VERSION) {
            this.removeAsync(key).catch(() => {});
            resolve(null);
            return;
          }

          // Check expiration
          const age = Date.now() - entry.timestamp;
          if (age > ttl) {
            this.removeAsync(key).catch(() => {});
            resolve(null);
            return;
          }

          resolve(entry.data);
        };

        request.onerror = () => {
          resolve(null);
        };
      });
    } catch {
      return null;
    }
  }

  /**
   * Get cached data from both localStorage and IndexedDB
   * Checks localStorage first (sync), then IndexedDB (async)
   * Returns the first available valid entry
   */
  static async getFromAll<T>(
    key: string,
    ttl: number = DEFAULT_TTL_MS
  ): Promise<T | null> {
    // Try localStorage first (synchronous, fast)
    const localData = this.get<T>(key, ttl);
    if (localData !== null) {
      return localData;
    }

    // Fallback to IndexedDB (async)
    return this.getAsync<T>(key, ttl);
  }

  /**
   * Set cached user data
   * Automatically chooses localStorage (<1MB) or IndexedDB (>1MB)
   */
  static set<T>(key: string, data: T): boolean {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };

    const serialized = JSON.stringify(entry);
    const size = serialized.length;

    // Use IndexedDB for large data (>1MB)
    if (size > MAX_LOCALSTORAGE_SIZE && isIndexedDBAvailable()) {
      // Store in IndexedDB asynchronously (don't block)
      this.setAsync(key, data).catch(() => {
        // Ignore errors silently
      });

      // Remove from localStorage only (don't remove from IndexedDB - we just stored there!)
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore errors silently
      }

      return true;
    }

    // Use localStorage for smaller data
    try {
      localStorage.setItem(key, serialized);

      // Remove from IndexedDB if it exists
      if (isIndexedDBAvailable()) {
        this.removeAsync(key).catch(() => {
          // Ignore errors silently
        });
      }

      return true;
    } catch (error) {
      // Handle quota exceeded errors
      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError'
      ) {
        // Try IndexedDB as fallback
        if (isIndexedDBAvailable()) {
          this.setAsync(key, data).catch(() => {
            // Ignore errors silently
          });
          this.remove(key);
          return true;
        }
      }

      return false;
    }
  }

  /**
   * Set cached data in IndexedDB (async)
   */
  static async setAsync<T>(key: string, data: T): Promise<boolean> {
    const dbPromise = this.getDB();
    if (!dbPromise) {
      return false;
    }

    try {
      const db = await dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const entry: CacheEntry<T> & { key: string } = {
        key,
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION,
      };

      return new Promise<boolean>((resolve) => {
        const request = store.put(entry);

        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }

  /**
   * Remove a specific cache entry
   * Removes from both localStorage and IndexedDB
   */
  static remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors silently
    }

    // Also try to remove from IndexedDB (async, don't wait)
    this.removeAsync(key).catch(() => {});
  }

  /**
   * Remove cache entry from IndexedDB (async)
   */
  static async removeAsync(key: string): Promise<void> {
    const dbPromise = this.getDB();
    if (!dbPromise) {
      return;
    }

    try {
      const db = await dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch {
      // Ignore errors silently
    }
  }

  /**
   * Clear all expired cache entries
   * @param companyId - Optional company ID to clear only that company's cache
   */
  static clearExpired(companyId?: string): void {
    const now = Date.now();

    // Clear expired from localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('users_')) {
          // Filter by companyId if provided
          if (companyId && !key.includes(`_${companyId}_`)) {
            return;
          }

          try {
            const entry = JSON.parse(localStorage.getItem(key) || '{}');
            if (entry.timestamp && now - entry.timestamp > DEFAULT_TTL_MS) {
              localStorage.removeItem(key);
            }
          } catch {
            // Remove invalid entries
            localStorage.removeItem(key);
          }
        }
      });
    } catch {
      // Ignore errors silently
    }

    // Clear expired from IndexedDB (async, don't wait)
    this.clearExpiredAsync(companyId).catch(() => {});
  }

  /**
   * Clear expired entries from IndexedDB (async)
   * @param companyId - Optional company ID to clear only that company's cache
   */
  static async clearExpiredAsync(companyId?: string): Promise<void> {
    const dbPromise = this.getDB();
    if (!dbPromise) {
      return;
    }

    try {
      const db = await dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const now = Date.now();

      await new Promise<void>((resolve) => {
        const request = index.openCursor();

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;

          if (cursor) {
            const entry = cursor.value as CacheEntry<unknown> & {
              key: string;
            };
            const age = now - entry.timestamp;

            // Filter by companyId if provided
            const shouldDelete =
              (age > DEFAULT_TTL_MS || entry.version !== CACHE_VERSION) &&
              (!companyId || entry.key.includes(`_${companyId}_`));

            if (shouldDelete) {
              cursor.delete();
            }

            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => {
          resolve();
        };
      });
    } catch {
      // Ignore errors silently
    }
  }

  /**
   * Clear all user cache entries (useful for logout or manual refresh)
   * Clears both localStorage and IndexedDB
   * @param companyId - Optional company ID to clear only that company's cache
   */
  static clearAll(companyId?: string): void {
    // Clear from localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('users_')) {
          // Filter by companyId if provided
          if (companyId && !key.includes(`_${companyId}_`)) {
            return;
          }

          localStorage.removeItem(key);
        }
      });
    } catch {
      // Ignore errors silently
    }

    // Clear from IndexedDB (async, don't wait)
    this.clearAllAsync(companyId).catch(() => {});
  }

  /**
   * Clear all entries from IndexedDB (async)
   * @param companyId - Optional company ID to clear only that company's cache
   */
  static async clearAllAsync(companyId?: string): Promise<void> {
    const dbPromise = this.getDB();
    if (!dbPromise) {
      return;
    }

    try {
      const db = await dbPromise;
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      if (companyId) {
        // Clear only entries for this company
        const request = store.openCursor();

        await new Promise<void>((resolve) => {
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
              .result;

            if (cursor) {
              const entry = cursor.value as { key: string };
              if (entry.key.includes(`_${companyId}_`)) {
                cursor.delete();
              }
              cursor.continue();
            } else {
              resolve();
            }
          };

          request.onerror = () => resolve();
        });
      } else {
        // Clear all entries
        await new Promise<void>((resolve) => {
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
        });
      }
    } catch {
      // Ignore errors silently
    }
  }

  /**
   * Get cache key for v3 users endpoint (scoped by company ID)
   */
  static getUsersV3Key(companyId: string, status: string): string {
    return CACHE_KEYS.USERS_V3(companyId, status);
  }

  /**
   * Get cache key for v4 users endpoint (scoped by company ID)
   */
  static getUsersV4Key(
    companyId: string,
    includeInactive: boolean,
    ignoreFiltering: boolean
  ): string {
    return CACHE_KEYS.USERS_V4(companyId, includeInactive, ignoreFiltering);
  }

  /**
   * Invalidate all user caches (call after user updates)
   * @param companyId - Optional company ID to invalidate only that company's cache
   */
  static invalidateAll(companyId?: string): void {
    this.clearAll(companyId);
  }

  /**
   * Get cache size in bytes (approximate)
   * Includes both localStorage and IndexedDB
   */
  static async getCacheSize(): Promise<number> {
    let total = 0;

    // Calculate localStorage size
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('users_')) {
          const value = localStorage.getItem(key);
          if (value) {
            total += key.length + value.length;
          }
        }
      });
    } catch {
      // Ignore errors
    }

    // Calculate IndexedDB size (async)
    try {
      const dbPromise = this.getDB();
      if (dbPromise) {
        const db = await dbPromise;
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        await new Promise<void>((resolve) => {
          request.onsuccess = () => {
            const entries = request.result as Array<
              CacheEntry<unknown> & { key: string }
            >;
            entries.forEach((entry) => {
              if (entry.key.startsWith('users_')) {
                const serialized = JSON.stringify(entry);
                total += serialized.length;
              }
            });
            resolve();
          };
          request.onerror = () => resolve();
        });
      }
    } catch {
      // Ignore errors
    }

    return total;
  }

  /**
   * Check if IndexedDB is available
   */
  static isIndexedDBAvailable(): boolean {
    return isIndexedDBAvailable();
  }
}

// Clear expired entries on module load (runs once per page load)
if (typeof window !== 'undefined') {
  UserCacheService.clearExpired();
  // UserCacheService.clearAll();
}

export default UserCacheService;
